/**
 * Home boat resolution for insight cards.
 * Reads `homeBoatName` from persistantStore and matches against sourcesStore.
 * Default home boat is GER when unset / unresolved.
 */

import { persistantStore } from '@store/persistantStore';
import { sourcesStore } from '@store/sourcesStore';
import { persistentSettingsService } from '@services/persistentSettingsService';
import { user } from '@store/userStore';
import { debug as logDebug, warn as logWarn } from '@utils/console';

/** Product default when the user has not picked another boat. */
export const DEFAULT_HOME_BOAT = 'GER';

/** Canonical source_name from the fleet matching `name` (case-insensitive), or null. */
function matchSourceName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) {
    return null;
  }
  if (!sourcesStore.isReady()) {
    return trimmed;
  }
  const id = sourcesStore.getSourceId(trimmed);
  if (id == null) {
    return null;
  }
  return sourcesStore.getSourceName(id) ?? trimmed;
}

/**
 * Canonical source_name for the home boat.
 * Uses the stored preference when it resolves to a fleet source; otherwise falls back to GER
 * when GER is in the fleet (or returns GER before sources load so the picker is never empty).
 */
export function resolveHomeBoatName(): string | null {
  const raw = String(persistantStore.homeBoatName() ?? '').trim();
  if (raw) {
    const matched = matchSourceName(raw);
    if (matched) {
      return matched;
    }
  }
  const ger = matchSourceName(DEFAULT_HOME_BOAT);
  if (ger) {
    return ger;
  }
  // Sources loaded and GER not present — no default.
  if (sourcesStore.isReady()) {
    return null;
  }
  return DEFAULT_HOME_BOAT;
}

/** Boat names available for the home-boat picker (current project sources). */
export function homeBoatOptions(): string[] {
  return sourcesStore
    .sources()
    .map((s) => String(s.source_name ?? '').trim())
    .filter((n) => n.length > 0)
    .sort((a, b) => a.localeCompare(b));
}

/**
 * If the store has no home boat yet, seed it to GER (when present) so pages start useful
 * without a picker click. Does not overwrite an explicit user choice.
 */
export async function ensureDefaultHomeBoat(): Promise<void> {
  const raw = String(persistantStore.homeBoatName() ?? '').trim();
  if (raw) {
    return;
  }
  if (!sourcesStore.isReady()) {
    // Still seed the store so the first paint / API save carry GER.
    persistantStore.setHomeBoatName(DEFAULT_HOME_BOAT);
    return;
  }
  const ger = matchSourceName(DEFAULT_HOME_BOAT);
  if (!ger) {
    return;
  }
  await setHomeBoatName(ger);
}

/**
 * Persist home boat name to store + user settings API.
 */
export async function setHomeBoatName(name: string): Promise<void> {
  const trimmed = String(name ?? '').trim() || DEFAULT_HOME_BOAT;
  persistantStore.setHomeBoatName(trimmed);

  const currentUser = user();
  const className = persistantStore.selectedClassName();
  const projectId = persistantStore.selectedProjectId();
  if (!currentUser?.user_id || !className || !projectId) {
    logDebug('[homeBoat] Saved locally only (no user/project for API sync)', { trimmed });
    return;
  }
  try {
    await persistentSettingsService.saveSettings(
      currentUser.user_id,
      className,
      projectId,
      { homeBoatName: trimmed }
    );
    logDebug('[homeBoat] Persisted homeBoatName', { trimmed });
  } catch (err) {
    logWarn('[homeBoat] Failed to persist homeBoatName to API', err);
  }
}

/** True when the fleet has enough boats for leader comparison. */
export function hasEnoughFleetBoats(rowCount: number): boolean {
  return rowCount >= 3;
}
